import { api } from './api';

export type AdminUserPayload = {
  email: string;
  name: string;
  role: string;
};

export type AdminOverviewResponse = {
  users_total?: number;
  passengers_total?: number;
  drivers_total?: number;
  fleet_managers_total?: number;
  vehicles_total?: number;
  assignments_total?: number;
  open_sos_incidents_total?: number;
  wallet_transactions_total?: number;
  wallet_transactions_today_total?: number;
  stats?: {
    totalUsers?: number;
    total_users?: number;
    activeDrivers?: number;
    active_drivers?: number;
    vehiclesOnPlatform?: number;
    vehicles_on_platform?: number;
    transactionsToday?: number;
    transactions_today?: number;
  };
  platformActivity?: AdminActivityItem[];
  platform_activity?: AdminActivityItem[];
  pendingActions?: AdminPendingAction[];
  pending_actions?: AdminPendingAction[] | AdminPendingActionTotals;
  recentSignups?: AdminSignup[];
  recent_signups?: AdminSignup[];
};

export type AdminActivityItem = {
  day?: string;
  label?: string;
  value?: number;
  count?: number;
  activity_date?: string;
  new_users_total?: number;
  wallet_transactions_total?: number;
  activity_total?: number;
};

export type AdminPendingAction = {
  title?: string;
  body?: string;
  description?: string;
  count?: number;
  tone?: string;
  icon?: string;
};

export type AdminPendingActionTotals = {
  kyc_reviews_total?: number;
  open_sos_incidents_total?: number;
  refund_requests_total?: number;
};

export type AdminSignup = {
  id?: string;
  username?: string;
  initials?: string;
  name?: string;
  fullName?: string;
  full_name?: string;
  email?: string;
  phone?: string | null;
  phone_number?: string | null;
  status?: string;
  role?: string;
  requested_role?: string | null;
  is_active?: boolean;
  is_phone_verified?: boolean;
  is_email_verified?: boolean;
  created_at?: string;
};

export type KycSubmission = {
  id: string;
  user_id: string;
  account_type: string;
  status: string;
  full_name: string;
  phone_number: string;
  national_id: string | null;
  licence_number: string | null;
  licence_expiry_date: string | null;
  company_name: string | null;
  company_reg_number: string | null;
  rejection_reason: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | KycReviewerPayload | null;
  reviewed_by_full_name?: string | null;
  reviewer?: KycReviewerPayload | null;
  reviewer_full_name?: string | null;
  documents?: KycDocumentPayload[];
  national_id_url?: string | null;
  national_id_document_url?: string | null;
  licence_url?: string | null;
  licence_document_url?: string | null;
  company_document_url?: string | null;
};

export type KycReviewerPayload = {
  full_name?: string;
  name?: string;
  username?: string;
};

export type KycDocumentPayload = {
  id?: string;
  name?: string;
  type?: string;
  document_type?: string;
  url?: string;
  file_url?: string;
  download_url?: string;
  content_type?: string;
  mime_type?: string;
};

export type KycQueueResponse = {
  submissions: KycSubmission[];
};

export type AdminPassenger = {
  id?: string;
  user_id?: string;
  full_name?: string;
  name?: string;
  username?: string;
  email?: string;
  phone_number?: string | null;
  phone?: string | null;
  status?: string;
  is_active?: boolean;
  is_email_verified?: boolean;
  is_phone_verified?: boolean;
  created_at?: string;
  joined_at?: string;
  trips_total?: number;
  total_trips?: number;
};

export type AdminPassengersResponse = {
  passengers?: AdminPassenger[];
  items?: AdminPassenger[];
  results?: AdminPassenger[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
};

export type AdminDriversResponse = {
  drivers?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  results?: Record<string, unknown>[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
};

export type AdminVehiclesResponse = {
  vehicles?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  results?: Record<string, unknown>[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
};

export type AdminFleetManagersResponse = {
  fleet_managers?: Record<string, unknown>[];
  managers?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  results?: Record<string, unknown>[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
};

export type AdminRoutesResponse = {
  routes?: Record<string, unknown>[];
  items?: Record<string, unknown>[];
  results?: Record<string, unknown>[];
  total?: number;
  count?: number;
  limit?: number;
  offset?: number;
};

export type CreateAdminRoutePayload = {
  route_code: string;
  name: string;
  origin_name: string;
  destination_name: string;
  fleet_id?: string;
  stops: {
    sequence: number;
    name: string;
    latitude: number;
    longitude: number;
    notes: string;
  }[];
  metadata: Record<string, unknown>;
};

export function getAdminOverview() {
  return api.get<AdminOverviewResponse>('/admin/dashboard/overview');
}

export function createAdminUser(payload: AdminUserPayload) {
  return api.post('/admin/users', payload);
}

export function updateAdminUser(userId: string, payload: Partial<AdminUserPayload>) {
  return api.patch(`/admin/users/${userId}`, payload);
}

export function setPassengerActive(userId: string, isActive: boolean, details?: { reason: string; notes: string }) {
  const action = isActive ? 'activate' : 'deactivate';
  return api.post(`/admin/dashboard/users/${userId}/${action}`, isActive ? undefined : details);
}

export function getKycQueue() {
  return api.get<KycQueueResponse>('/api/v1/onboarding/admin/kyc');
}

export function approveKycRequest(userId: string) {
  return api.post(`/api/v1/onboarding/admin/kyc/${userId}/approve`);
}

export function rejectKycRequest(userId: string, reason: string) {
  return api.post(`/api/v1/onboarding/admin/kyc/${userId}/reject`, { reason });
}

export function getPassengers(limit = 50, offset = 0, status?: string) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('status', status);
  return api.get<AdminPassengersResponse>(`/admin/dashboard/passengers?${params.toString()}`);
}

export function getAdminDrivers(filters: {
  status?: string;
  accountType?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.accountType) params.set('account_type', filters.accountType);
  if (filters.search) params.set('search', filters.search);
  params.set('limit', String(filters.limit ?? 50));
  params.set('offset', String(filters.offset ?? 0));
  return api.get<AdminDriversResponse>(`/admin/dashboard/drivers?${params.toString()}`);
}

export function getAdminVehicles(filters: {
  status?: string;
  type?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  params.set('limit', String(filters.limit ?? 50));
  params.set('offset', String(filters.offset ?? 0));
  return api.get<AdminVehiclesResponse>(`/admin/dashboard/vehicles?${params.toString()}`);
}

export function getFleetManagers(filters: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  params.set('limit', String(filters.limit ?? 50));
  params.set('offset', String(filters.offset ?? 0));
  return api.get<AdminFleetManagersResponse>(`/admin/dashboard/fleet-managers?${params.toString()}`);
}

export function setVehicleActive(vehicleId: string, isActive: boolean, details?: { reason: string; notes: string }) {
  const action = isActive ? 'activate' : 'deactivate';
  return api.post(`/admin/dashboard/vehicles/${vehicleId}/${action}`, isActive ? undefined : details);
}

export function getTransactions() {
  return api.get('/admin/transactions');
}

export function getAdminRoutes(filters: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('search', filters.search);
  params.set('limit', String(filters.limit ?? 50));
  params.set('offset', String(filters.offset ?? 0));
  return api.get<AdminRoutesResponse>(`/admin/dashboard/routes?${params.toString()}`);
}

export function setRouteActive(routeId: string, isActive: boolean, details?: { reason: string; notes: string }) {
  const action = isActive ? 'activate' : 'deactivate';
  return api.post(`/admin/routes/${routeId}/${action}`, isActive ? undefined : details);
}

export function createAdminRoute(payload: CreateAdminRoutePayload) {
  return api.post('/admin/routes', payload);
}

export function updateAdminRoute(routeId: string, payload: CreateAdminRoutePayload) {
  return api.patch(`/admin/routes/${routeId}`, payload);
}

export function getAdminRoute(routeId: string) {
  return api.get<Record<string, unknown>>(`/admin/routes/${routeId}`);
}
